import { useCallback } from "react";
import CommonValidatorProps from "./commonProps";
import useValidator from "./useValidator";
import { useTranslation } from "react-i18next";

export interface PasswordValidatorProps extends CommonValidatorProps<string> {
  required?: boolean;
  upperCaseChars?: number;
  lowerCaseChars?: number;
  numbers?: number;
  specialChars?: number;
  minimumChars?: number;
}

const usePasswordValidator = (props: PasswordValidatorProps) => {
  const { t } = useTranslation();
  const { required, upperCaseChars, lowerCaseChars, numbers, specialChars, minimumChars } = props;

  const defaultUppercase = 1;
  const defaultLowercase = 1;
  const defaultNumbers = 1;
  const defaultSpecialChars = 1;
  const defaultMinChars = 10;

  const regexUppercase = new RegExp(`(?:.*[A-Z]){${upperCaseChars || defaultUppercase}}`);
  const regexLowercase = new RegExp(`(?:.*[a-z]){${lowerCaseChars || defaultLowercase}}`);
  const regexSpecialChat = new RegExp(
    `(?:.*[!@#$%^&*()_+\\-=[\\]{};':"\\\\|,.<>/?]){${specialChars || defaultSpecialChars}}`
  );
  const regexNumbers = new RegExp(`(?:.*[0-9]){${numbers || defaultNumbers}}`);

  const validateRequired = useCallback(
    (value?: string): [boolean, string] => {
      return [required ? value !== undefined : true, t("field:helper.fieldIsRequired")];
    },
    [required]
  );

  const validateUppercase = useCallback(
    (value?: string): [boolean, string] => {
      let number = value?.split("").filter((char) => /[A-Z]/.test(char) && isNaN(parseInt(char))).length || 0;
      return [
        regexUppercase.test(value || ""),
        `${(upperCaseChars || defaultUppercase) - number} uppercase ${
          (upperCaseChars || defaultUppercase) - number > 1 ? "chars" : "char"
        } required`,
      ];
    },
    [upperCaseChars]
  );

  const validateLowercase = useCallback(
    (value?: string): [boolean, string] => {
      let number = value?.split("").filter((char) => /[a-z]/.test(char) && isNaN(parseInt(char))).length || 0;
      return [
        regexLowercase.test(value || ""),
        `${(lowerCaseChars || defaultLowercase) - number} lowercase ${
          (lowerCaseChars || defaultLowercase) - number > 1 ? "chars" : "char"
        } required`,
      ];
    },
    [lowerCaseChars]
  );

  const validateNumbers = useCallback(
    (value?: string): [boolean, string] => {
      let number = value?.split("").filter((char) => /\d/.test(char)).length || 0;
      return [
        regexNumbers.test(value!),
        `${(numbers || defaultNumbers) - number} ${
          (numbers || defaultNumbers) - number > 1 ? "numbers" : "number"
        } required`,
      ];
    },
    [numbers]
  );

  const validateSpecial = useCallback(
    (value?: string): [boolean, string] => {
      let number = value?.split("").filter((char) => !char.match(/[a-zA-Z0-9\s]/)).length || 0;
      return [
        regexSpecialChat.test(value || ""),
        `${(specialChars || defaultSpecialChars) - number} special ${
          (specialChars || defaultSpecialChars) - number > 1 ? "chars" : "char"
        }  required`,
      ];
    },
    [specialChars]
  );

  const validateMinimum = useCallback(
    (value?: string): [boolean, string] => {
      return [
        value!.length >= (minimumChars || defaultMinChars),
        `Minimun of ${(minimumChars || defaultMinChars) - value!.length} ${
          (minimumChars || defaultMinChars) - value!.length > 1 ? "chars" : "char"
        }`,
      ];
    },
    [minimumChars]
  );

  const validator = useValidator({
    ...props,
    validators: [
      validateRequired,
      validateUppercase,
      validateLowercase,
      validateNumbers,
      validateSpecial,
      validateMinimum,
    ],
  });

  return validator;
};

export default usePasswordValidator;
